import {
  Button,
  Container,
  Grid,
  Typography,
  // useMediaQuery,
  useTheme,
  TextField,
  Box,
  Paper,
  InputAdornment,
} from "@mui/material";
import { Email } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/logo1.png";

import { useForgetPassword } from "../../hooks/query/useUser";

// Validation schema for email or phone
const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Please enter a valid email address" }),
});

const ForgotPassword = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const { mutateAsync: forgetPassword } = useForgetPassword();

  const onSubmit = async (data: any) => {
    // try {
    // forgetPassword(data);
    // toast.success("Password recovery link sent successfully!");
    // navigate("/reset-password");
    // } catch (error: any) {
    // toast.error(error.body.error.message);
    // }
    console.log("Data", data);
    toast.promise(
      new Promise((resolve, reject) => {
        try {
          forgetPassword(data, {
            onSuccess: (response) => {
              resolve(response);
              navigate("/password-reset-link-success");
            },
            onError: (error: any) => {
              const errorMessage =
                error?.body?.error?.message || "An unknown error occurred.";
              console.log("Error message", errorMessage);
              reject(new Error(errorMessage));
            },
          });
        } catch (error: any) {
          const errorMessage =
            error?.body?.error?.message || "An unknown error occurred.";
          console.log("Error message", errorMessage);
          reject(new Error(errorMessage));
        }
      }),
      {
        pending: "Sending password reset link...",
        success: "Password reset link sent successfully! 🎉",
        error: {
          render({ data }: { data: any }) {
            console.log("Data", data.body);
            // 'data' is the error object from the rejected promise
            return (
              data?.message ||
              "Failed to send password reset link. Please try again."
            );
          },
        },
      }
    );
  };

  // const toggleIdentifierType = () => {
  // setIdentifierType(identifierType === "email" ? "phone" : "email");
  // };

  return (
    <Container
      maxWidth="lg"
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: "450px",
          borderRadius: theme.spacing(3),
          padding: theme.spacing(4),
          background: "linear-gradient(145deg, #f0f4f8 0%, #e6eaf0 100%)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-50px",
            left: "-50px",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #bb0000 0%, #bb0000 100%)",
            opacity: 0.1,
          }}
        />
        <Box
          sx={{
            textAlign: "center",
            marginBottom: theme.spacing(3),
          }}
        >
          <img
            src={logo}
            alt="logo"
            style={{
              width: "200px",
              height: "200px",
              marginBottom: theme.spacing(2),
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(1),
            }}
          >
            Forgot Password
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Enter your email to reset your password
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Email"
                    variant="outlined"
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* <IconButton
onClick={toggleIdentifierType}
edge="start"
> */}
                          <Email />
                          {/* </IconButton> */}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: theme.spacing(2),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  mt: 2,
                  mb: 2,
                  borderRadius: theme.spacing(2),
                  padding: theme.spacing(1.5),
                  background:
                    "linear-gradient(45deg, #bb0000 0%, #bb0000 100%)",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0 6px 8px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {isSubmitting ? "Sending..." : "Send Recovery Link"}
              </Button>
            </Grid>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Button
                variant="text"
                onClick={() => navigate("/login")}
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: "none",
                }}
              >
                Back to Login
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;
